import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

/**
 * Referral capture route — `/r/{code}`.
 *
 * Persists the referral code in BOTH `localStorage` (long-lived) and a cookie
 * (so SSR/server-rendered hits also see it), then redirects to the landing
 * page with `?ref=CODE` as a query-param backup.
 *
 * The referral is tied to the user's account by an authenticated POST to
 * `/api/referral/capture` after sign-in (handled in the post-login flow or
 * any page that calls `RefferalAutoAttach`).
 */
export default function ReferralCapturePage() {
  const { code } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Normalize to lowercase letters + digits. Legacy uppercase codes still
    // resolve server-side via case-insensitive lookup — we just stop creating
    // mixed-case representations going forward.
    const safe = (code || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (safe) {
      try {
        localStorage.setItem("qib_referral_code", safe);
        // 30-day cookie with SameSite=Lax so server-rendered routes can read
        const maxAge = 60 * 60 * 24 * 30;
        document.cookie = `qib_referral_code=${safe}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
      } catch {
        /* private mode / cookies disabled — ignore */
      }
    }
    // PROMPT 026 — Forward to pricing (with the code as a fallback query
    // param). New users must pick a plan before any account exists; the
    // referral cookie + localStorage entry survive the round-trip and
    // gets applied at checkout / on webhook account creation.
    navigate(safe ? `/pricing?ref=${encodeURIComponent(safe)}` : "/pricing", { replace: true });
  }, [code, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500" data-testid="referral-capture-page">
      Saving your invite…
    </div>
  );
}

/**
 * Tiny helper — read the persisted referral code from any place. Returns
 * `""` if absent / invalid. Used by post-login screens to call
 * `/api/referral/capture` once.
 */
export function getStoredReferralCode() {
  try {
    const ls = (localStorage.getItem("qib_referral_code") || "").trim().toLowerCase();
    if (ls) return ls;
    // Match both legacy uppercase and new lowercase persisted values.
    const m = document.cookie.match(/(?:^|; )qib_referral_code=([A-Za-z0-9]+)/);
    return m ? m[1].toLowerCase() : "";
  } catch {
    return "";
  }
}

export function clearStoredReferralCode() {
  try {
    localStorage.removeItem("qib_referral_code");
    document.cookie = "qib_referral_code=; Max-Age=0; Path=/";
  } catch {
    /* ignore */
  }
}
